import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { listClients, listProjects, createFreeInvoice } from "../../lib/api";
import { TAX_REGIME_KEYS, TAX_REGIME_LABELS, TAX_REGIME_HINTS } from "../../lib/taxRegimes";
import InvoiceLinesEditor from "./InvoiceLinesEditor";

// v14t — Regime → effectief BTW-tarief mapping (matcht backend).
const REGIME_TO_VAT = { standard: 21, reduced_renovation: 6, reverse_charge: 0 };

// v6 F-Invoicing F2 — Vrije factuur (Flow B).
// Geen invloed op quote_status of remaining. Klant verplicht, project optioneel.
// v14t — Regime-select toegevoegd: bepaalt vat_rate op alle regels + initiële berekening.
export default function VrijeFactuurDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clientId, setClientId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [regime, setRegime] = useState("standard");
  const effectiveVat = REGIME_TO_VAT[regime] ?? 21;
  const isReverseCharge = regime === "reverse_charge";
  const [lines, setLines] = useState([
    { description: "", quantity: 1, unit: "fixed", unit_price_excl: 0, vat_rate: 21, line_type: "service", sort_order: 0 },
  ]);
  const [submitting, setSubmitting] = useState(false);

  // v14t — Regime wisselt → force vat_rate op ALLE bestaande regels.
  // Zo blijft preview/totals meteen consistent met wat de backend zal opslaan.
  useEffect(() => {
    setLines((prev) => prev.map((ln) => ({ ...ln, vat_rate: effectiveVat })));
    // effectiveVat dependency, niet regime — voorkomt extra render bij idem-set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveVat]);

  useEffect(() => {
    if (!open) return;
    listClients().then(setClients).catch(() => toast.error("Klanten laden mislukt."));
  }, [open]);

  useEffect(() => {
    if (!clientId) { setProjects([]); setProjectId(""); return; }
    listProjects(clientId).then(setProjects).catch(() => setProjects([]));
  }, [clientId]);

  const totals = useMemo(() => {
    let sub = 0, vat = 0;
    for (const ln of lines) {
      const raw = Number(ln.quantity || 0) * Number(ln.unit_price_excl || 0);
      const excl = ln.line_type === "discount" ? -Math.abs(raw) : raw;
      if (ln.line_type === "option") continue;
      sub += excl;
      vat += excl * Number(ln.vat_rate || 0) / 100;
    }
    // v14t — reverse_charge: buckets → 0, total_incl = subtotal (matcht backend).
    if (isReverseCharge) {
      return {
        subtotal: Math.round(sub * 100) / 100,
        vat: 0,
        incl: Math.round(sub * 100) / 100,
      };
    }
    return {
      subtotal: Math.round(sub * 100) / 100,
      vat: Math.round(vat * 100) / 100,
      incl: Math.round((sub + vat) * 100) / 100,
    };
  }, [lines, isReverseCharge]);

  const fmtEur = (n) => new Intl.NumberFormat("nl-BE", { style: "currency", currency: "EUR" }).format(Number(n || 0));

  const reset = () => {
    setClientId(""); setProjectId(""); setProjects([]);
    setRegime("standard");
    setLines([{ description: "", quantity: 1, unit: "fixed", unit_price_excl: 0, vat_rate: 21, line_type: "service", sort_order: 0 }]);
  };

  const canSubmit = clientId && lines.some((ln) => (ln.description || "").trim() && Number(ln.unit_price_excl || 0) !== 0);

  const onSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const inv = await createFreeInvoice({
        client_id: clientId,
        project_id: projectId || null,
        invoice_type: "free",
        line_items: lines,
        tax_regime_key: regime,
        notes: "",
      });
      toast.success("Vrije concept-factuur aangemaakt.");
      onOpenChange(false);
      reset();
      navigate(`/facturen/${inv.id}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Aanmaken mislukt.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent
        className="max-w-4xl w-[calc(100vw-1rem)] sm:w-full p-0 max-h-[95dvh] sm:max-h-[90vh] overflow-hidden flex flex-col gap-0"
        data-testid="vrije-factuur-dialog"
      >
        {/* Sticky header — bevat mobiele "← Facturen" knop, titel, Radix X */}
        <div className="flex-shrink-0 border-b border-[#E5E2DC] bg-white px-4 sm:px-6 py-3 sm:py-4">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="md:hidden inline-flex items-center gap-1 text-sm text-[#5D7259] font-semibold hover:underline mb-2 -ml-1"
            data-testid="vrije-factuur-back-mobile"
          >
            <ArrowLeft className="w-4 h-4" /> Facturen
          </button>
          <DialogTitle className="font-heading text-xl sm:text-2xl text-[#2C2C2A] pr-8">Vrije factuur</DialogTitle>
          <DialogDescription className="text-xs sm:text-sm text-[#4F4F4D] mt-1">
            Een factuur zonder link naar een offerte — bv. voor kleine werken of nakomende posten.
          </DialogDescription>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#2C2C2A]">Klant *</Label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="vrije-factuur-client">
                  <SelectValue placeholder="Kies een klant" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#2C2C2A]">Project <span className="text-xs text-[#A8A7A0]">(optioneel)</span></Label>
              <Select value={projectId || "none"} onValueChange={(v) => setProjectId(v === "none" ? "" : v)} disabled={!clientId}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="vrije-factuur-project">
                  <SelectValue placeholder={clientId ? "Kies een project" : "Kies eerst een klant"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Geen project —</SelectItem>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="vf-regime" className="text-sm font-medium text-[#2C2C2A]">BTW-regime</Label>
            <select
              id="vf-regime"
              value={regime}
              onChange={(e) => setRegime(e.target.value)}
              className="flex h-11 w-full rounded-xl border border-[#E5E2DC] bg-white px-3 text-sm"
              data-testid="vrije-factuur-regime"
            >
              {TAX_REGIME_KEYS.map((k) => (
                <option key={k} value={k}>{TAX_REGIME_LABELS[k]}</option>
              ))}
            </select>
            <p className="text-[11px] text-[#6E6E6C] leading-snug">
              {TAX_REGIME_HINTS[regime]}
            </p>
            <p className="text-[11px] text-[#6E6E6C] leading-snug italic">
              Bepaalt het BTW-tarief van nieuwe regels: Standaard → 21%, Renovatie → 6%, Verlegd → 0%. Je kan het BTW-tarief per regel achteraf nog bijstellen.
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium text-[#2C2C2A]">Factuurregels</Label>
            <InvoiceLinesEditor lines={lines} onChange={setLines} defaultVat={effectiveVat} />
          </div>

          <div className="rounded-xl bg-[#FAF9F6] border border-[#E5E2DC] p-4" data-testid="vrije-factuur-totals">
            <div className="grid grid-cols-2 gap-1 text-sm">
              <div className="text-[#4F4F4D]">Subtotaal excl.</div>
              <div className="text-right text-[#2C2C2A]">{fmtEur(totals.subtotal)}</div>
              <div className="text-[#4F4F4D]">{isReverseCharge ? "BTW (verlegd)" : "Totaal BTW"}</div>
              <div className="text-right text-[#2C2C2A]">{fmtEur(totals.vat)}</div>
              <div className="text-[#2C2C2A] font-medium pt-1 border-t border-[#E5E2DC]">Totaal incl.</div>
              <div className="text-right font-bold text-[#2C2C2A] pt-1 border-t border-[#E5E2DC]">{fmtEur(totals.incl)}</div>
            </div>
          </div>
        </div>

        {/* Sticky footer */}
        <div className="flex-shrink-0 border-t border-[#E5E2DC] bg-white px-4 sm:px-6 py-3 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="h-11 rounded-xl"
            data-testid="vrije-factuur-cancel"
          >
            Annuleer
          </Button>
          <Button
            onClick={onSubmit} disabled={!canSubmit || submitting}
            className="h-11 rounded-xl bg-[#2C2C2A] hover:bg-[#1E1E1D] text-white"
            data-testid="vrije-factuur-submit"
          >
            {submitting ? "Bezig…" : "Concept aanmaken"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
